import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import AuthLayout from "./AuthLayout";

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSignIn = useCallback(
    async (e: React.FormEvent) => {
      console.log("[handleSignIn] Form submit");
      e.preventDefault();
      setError(null);
      setIsLoading(true);
      console.log("[handleSignIn] Started sign-in flow with:", {
        email,
        password,
      });

      try {
        // First, sign in with email and password
        console.log("[handleSignIn] Calling supabase.auth.signInWithPassword");
        const { data: authData, error: authError } =
          await supabase.auth.signInWithPassword({
            email,
            password,
          });
        console.log("[handleSignIn] Sign in result:", { authData, authError });

        if (authError) {
          console.log("[handleSignIn] authError:", authError.message);
          setError(authError.message);
          toast.error("Sign in failed", { description: authError.message });
          return;
        }

        if (!authData.user) {
          console.log("[handleSignIn] authData.user is null");
          setError("Failed to retrieve user information");
          toast.error("Sign in failed", {
            description: "Failed to retrieve user information",
          });
          return;
        }

        console.log("[handleSignIn] User signed in:", authData.user);

        // Wait a bit for session to be fully established
        await new Promise(resolve => setTimeout(resolve, 100));

        // Get the current session to ensure it's properly established
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session) {
          console.error("[handleSignIn] Session error:", sessionError);
          setError("Failed to establish session. Please try again.");
          toast.error("Sign in failed", {
            description: "Failed to establish session",
          });
          return;
        }

        // Then, check if the user has admin role - optimized query
        console.log("[handleSignIn] Querying users table for role");
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("role")
          .eq("auth_id", authData.user.id)
          .maybeSingle();

        console.log("[handleSignIn] User role query result:", {
          userData,
          userError,
        });

        if (userError) {
          console.error(
            "[handleSignIn] userError while checking role:",
            userError
          );
          // If there's an error checking role, still allow login but log the error
          console.warn("[handleSignIn] Proceeding with login despite role check error");
          toast.success("Signed in successfully");
          navigate("/dashboard");
          return;
        }

        // Check if the user has admin role
        if (userData?.role !== "admin") {
          console.log("[handleSignIn] User is not admin, signing out");
          await supabase.auth.signOut();
          setError("Access denied. Only admin users can access this portal.");
          toast.error("Access denied", {
            description: "Only admin users can access this portal.",
          });
          return;
        }

        console.log("[handleSignIn] User is admin, navigating to dashboard");
        toast.success("Signed in successfully");
        navigate("/dashboard");
      } catch (err) {
        console.error("[handleSignIn] Unexpected error during sign in:", err);
        setError("An unexpected error occurred. Please try again.");
        toast.error("Sign in failed", {
          description: "An unexpected error occurred",
        });
      } finally {
        console.log("[handleSignIn] Sign-in flow complete");
        setIsLoading(false);
      }
    },
    [email, password, navigate]
  );

  return (
    <AuthLayout
      title="Sign in to your account"
      subtitle="Enter your admin credentials to access your dashboard"
    >
      <form onSubmit={handleSignIn} className="mt-8 space-y-6">
        {error && (
          <div className="rounded-md bg-destructive/10 p-3">
            <div className="text-sm text-destructive">{error}</div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1"
              placeholder="admin@example.com"
              disabled={isLoading}
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <div className="text-sm">
                <Link
                  to="/auth/forgot-password"
                  className="font-medium text-primary hover:underline"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1"
              disabled={isLoading}
            />
          </div>
        </div>

        <div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current mr-2"></div>
                Signing in...
              </div>
            ) : (
              "Sign in"
            )}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}